import { DocsLayout } from "@/components/docs-layout";
import { DocsNavigation } from "@/components/docs-navigation";
import { AlertTriangle, TrendingDown, Info } from "lucide-react";

export const MarginLiquidation = () => {
  return (
    <DocsLayout>
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-3xl font-bold mb-2">Margin & Liquidation</h1>
        
        <div className="text-sm text-muted-foreground mb-8">
          <p>Understand margin requirements, liquidation mechanics, and how to avoid liquidation</p>
        </div>

        {/* What Is Margin */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">What Is Margin?</h2>
          <p className="text-muted-foreground mb-6">
            Margin is the collateral (USDC) you deposit to open and maintain leveraged positions. 
            There are two types of margin on Nordex:
          </p>
          
          <div className="grid gap-4 md:grid-cols-2 mb-6">
            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">Initial Margin</h3>
              <p className="text-sm text-muted-foreground mb-2">
                The minimum amount of collateral required to open a position.
              </p>
              <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg text-sm">
                <strong>On Nordex:</strong> Determined by your leverage (e.g., 10% at 10x, 1% at 100x)
              </div>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">Maintenance Margin</h3>
              <p className="text-sm text-muted-foreground mb-2">
                The minimum amount needed to keep a position open. Fall below this and you'll be liquidated.
              </p>
              <div className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg text-sm">
                <strong>On Nordex:</strong> Varies by leverage tier (lower leverage = lower maintenance requirement)
              </div>
            </div>
          </div>
        </section>

        {/* Margin Calculation */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Understanding Your Margin</h2>
          
          <p className="text-muted-foreground mb-6">
            Your account displays several margin-related values in the Balances panel:
          </p>

          <div className="space-y-4">
            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Total Collateral</h3>
              <p className="text-sm text-muted-foreground">
                The total USDC you've deposited into your trading account.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Used Margin</h3>
              <p className="text-sm text-muted-foreground">
                The amount of collateral locked in your open positions. This cannot be withdrawn 
                while positions are open.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Available Balance</h3>
              <p className="text-sm text-muted-foreground">
                Free collateral available to open new positions or add to existing ones. 
                <strong className="text-foreground"> Available = Total Collateral - Used Margin + Unrealized P&L</strong>
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Total Equity</h3>
              <p className="text-sm text-muted-foreground">
                Your current account value including unrealized profit/loss. 
                <strong className="text-foreground"> Equity = Total Collateral + Unrealized P&L</strong>
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Margin Ratio</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Measures account health. <strong className="text-foreground">Margin Ratio = (Total Equity / Used Margin) × 100%</strong>
              </p>
              <ul className="text-sm text-muted-foreground space-y-1 pl-4 list-disc">
                <li><strong className="text-emerald-500">{'>'}= 250%:</strong> Safe -- normal trading</li>
                <li><strong className="text-blue-500">150-250%:</strong> Caution -- monitor closely</li>
                <li><strong className="text-amber-500">110-150%:</strong> Warning -- take action to add collateral or reduce positions</li>
                <li><strong className="text-red-500">{'<'} 110%:</strong> Danger -- liquidation is imminent</li>
                <li><strong className="text-red-500">{'<'}= 100%:</strong> Liquidation triggered -- positions are closed automatically</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Example */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Margin Example</h2>
          
          <div className="p-6 border border-border rounded-lg bg-card">
            <h3 className="font-semibold mb-4">Scenario:</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                <span className="text-muted-foreground">Initial Deposit (Total Collateral):</span>
                <span className="font-semibold">$10,000</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                <span className="text-muted-foreground">Open Position with 10x leverage:</span>
                <span className="font-semibold">$50,000 size</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                <span className="text-muted-foreground">Used Margin (collateral for position):</span>
                <span className="font-semibold">$5,000</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                <span className="text-muted-foreground">Available Balance:</span>
                <span className="font-semibold">$5,000</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-primary/10 border border-primary/20">
                <span className="font-semibold">Margin Ratio:</span>
                <span className="font-bold text-primary">200%</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              Calculation: ($10,000 / $5,000) × 100% = 200%
            </p>
          </div>
        </section>

        {/* What Is Liquidation */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">What Is Liquidation?</h2>
          
          <p className="text-muted-foreground mb-6">
            Liquidation occurs when your margin ratio falls to 100% or below. 
            The platform automatically closes your position to prevent further losses and protect the remaining collateral.
          </p>

          <div className="p-6 border border-red-500/20 rounded-lg bg-red-500/5 mb-6">
            <div className="flex gap-3">
              <AlertTriangle className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold mb-2">Why Liquidation Happens</h3>
                <p className="text-sm text-muted-foreground">
                  When the market moves against your position, your unrealized losses increase. This reduces 
                  your total equity. If your equity falls too low relative to your used margin, the system 
                  liquidates your position to prevent your account from going negative.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Liquidation Price */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Understanding Liquidation Price</h2>
          
          <p className="text-muted-foreground mb-6">
            The liquidation price is shown when you place an order and in your open positions. 
            It's the price at which your position will be automatically closed.
          </p>

          <div className="grid gap-4 md:grid-cols-2 mb-6">
            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <TrendingDown className="h-6 w-6 text-emerald-500 rotate-180" />
                </div>
                <h3 className="font-semibold text-lg">Long Position</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-2">
                Liquidation happens if price drops too far below entry.
              </p>
              <div className="p-3 bg-muted/30 rounded-lg text-sm font-mono">
                Liq Price {'<'} Entry Price
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Example: Long BTC at $50,000 with 10x leverage → Liquidation ~$45,000
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-red-500/10">
                  <TrendingDown className="h-6 w-6 text-red-500" />
                </div>
                <h3 className="font-semibold text-lg">Short Position</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-2">
                Liquidation happens if price rises too far above entry.
              </p>
              <div className="p-3 bg-muted/30 rounded-lg text-sm font-mono">
                Liq Price {'>'} Entry Price
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Example: Short ETH at $3,000 with 10x leverage → Liquidation ~$3,300
              </p>
            </div>
          </div>

          <div className="p-4 bg-muted/30 rounded-lg">
            <div className="flex gap-3">
              <Info className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                Higher leverage = Liquidation price closer to entry price. At the technical maximum of 100x leverage 
                (available on select markets only), even a 1% 
                move can liquidate your position. At 2x leverage, you can withstand a 40% move.
              </p>
            </div>
          </div>
        </section>

        {/* Margin Modes */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Isolated vs Cross Margin</h2>
          
          <p className="text-muted-foreground mb-6">
            Nordex supports two margin modes that affect how liquidation is calculated:
          </p>

          <div className="grid gap-4 md:grid-cols-2 mb-6">
            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">Isolated Margin</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Each position has its own isolated margin. If one position is liquidated, it does not 
                affect your other positions or available balance.
              </p>
              <p className="text-sm text-muted-foreground">
                Liquidation is triggered per-position when the mark price crosses the position's liquidation price.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">Cross Margin</h3>
              <p className="text-sm text-muted-foreground mb-2">
                All positions share your full account equity. Your entire available balance acts as 
                collateral for all open positions.
              </p>
              <p className="text-sm text-muted-foreground">
                Liquidation is triggered at the account level when total equity falls below total maintenance margin. 
                Riskiest positions are closed first until the account is healthy.
              </p>
            </div>
          </div>

          <p className="text-sm text-muted-foreground">
            You can switch between margin modes in the order form. Note: you must close all open positions before switching modes.
          </p>
        </section>

        {/* Maintenance Margin Tiers */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Maintenance Margin Tiers</h2>
          
          <p className="text-muted-foreground mb-6">
            The maintenance margin rate varies based on your leverage level:
          </p>

          <div className="overflow-x-auto mb-6">
            <table className="w-full border border-border rounded-lg">
              <thead className="bg-muted/50">
                <tr>
                  <th className="p-4 text-left font-semibold">Leverage Range</th>
                  <th className="p-4 text-left font-semibold">Maintenance Margin</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                <tr>
                  <td className="p-4 text-sm">1x - 19x</td>
                  <td className="p-4 text-sm">1.0%</td>
                </tr>
                <tr>
                  <td className="p-4 text-sm">20x - 49x</td>
                  <td className="p-4 text-sm">0.75%</td>
                </tr>
                <tr>
                  <td className="p-4 text-sm">50x+</td>
                  <td className="p-4 text-sm">0.4%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Liquidation Penalty & Insurance Fund */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Liquidation Penalty & Insurance Fund</h2>
          
          <p className="text-muted-foreground mb-4">
            When a position is liquidated, a small penalty fee of <strong className="text-foreground">0.05%</strong> of 
            the position value is applied. This penalty helps maintain the platform's insurance fund.
          </p>
          <p className="text-muted-foreground mb-4">
            The insurance fund covers losses in situations where a liquidated position's remaining margin 
            is insufficient. Any remaining margin after covering losses and the penalty fee is contributed 
            to the insurance fund.
          </p>
        </section>

        {/* Avoiding Liquidation */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">How to Avoid Liquidation</h2>
          
          <div className="space-y-4">
            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">1. Use lower leverage</h3>
              <p className="text-sm text-muted-foreground">
                The most effective way to avoid liquidation. Lower leverage gives your 
                position more room to breathe during market volatility.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">2. Monitor your margin ratio</h3>
              <p className="text-sm text-muted-foreground">
                Keep an eye on your margin ratio in the Balances panel. If it drops below 150%, 
                take action immediately by adding collateral or reducing positions.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">3. Add collateral</h3>
              <p className="text-sm text-muted-foreground">
                If your position is moving against you and liquidation risk is increasing, deposit more 
                USDC to increase your equity and improve your margin ratio.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">4. Close or reduce positions</h3>
              <p className="text-sm text-muted-foreground">
                If you can't add more collateral, consider closing losing positions or reducing 
                position sizes to free up margin and reduce liquidation risk.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">5. Set price alerts</h3>
              <p className="text-sm text-muted-foreground">
                Use price alerts or monitoring tools to notify you when prices approach your liquidation 
                levels, so you can take preventive action.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">6. Don't overtrade</h3>
              <p className="text-sm text-muted-foreground">
                Avoid using all your available balance for positions. Keep a buffer of unused collateral 
                (at least 20-30%) to handle adverse price movements.
              </p>
            </div>
          </div>
        </section>

        {/* Liquidation Process */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">What Happens During Liquidation</h2>
          
          <div className="space-y-3">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center font-bold text-red-500">
                1
              </div>
              <div>
                <h3 className="font-semibold mb-1">Liquidation Triggered</h3>
                <p className="text-sm text-muted-foreground">
                  When your margin ratio falls to 100% or below, the liquidation process begins automatically.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center font-bold text-red-500">
                2
              </div>
              <div>
                <h3 className="font-semibold mb-1">Position Closed</h3>
                <p className="text-sm text-muted-foreground">
                  Your position is immediately closed at the current market price (which may be worse 
                  than your calculated liquidation price due to slippage).
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center font-bold text-red-500">
                3
              </div>
              <div>
                <h3 className="font-semibold mb-1">Loss Realized</h3>
                <p className="text-sm text-muted-foreground">
                  The loss from the position is deducted from your collateral. In most cases, you'll 
                  retain some collateral, but in volatile markets, you may lose more than expected.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center font-bold text-red-500">
                4
              </div>
              <div>
                <h3 className="font-semibold mb-1">Remaining Balance</h3>
                <p className="text-sm text-muted-foreground">
                  Any remaining collateral stays in your account and can be used for new positions 
                  or withdrawn.
                </p>
              </div>
            </div>
          </div>
        </section>

        <DocsNavigation 
          previous={{ title: "Leverage Trading Explained", href: "/leverage" }}
          next={{ title: "Funding Rates", href: "/funding-rates" }}
        />
      </div>
    </DocsLayout>
  );
};

